import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  FormHelperText,
  ListSubheader,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';

interface UserFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  roleSystemKey: string;
  organizationId?: string;
  storeId?: string;
}

// System role definitions for super-admin user creation
const SYSTEM_ROLES = [
  { systemKey: 'super_admin', name: 'Super Admin', category: 'administrative' },
  { systemKey: 'org_owner', name: 'Organization Owner', category: 'administrative' },
  { systemKey: 'store_owner', name: 'Store Owner', category: 'administrative' },
  { systemKey: 'store_manager', name: 'Store Manager', category: 'operational' },
  { systemKey: 'warehouse_manager', name: 'Warehouse Manager', category: 'operational' },
  { systemKey: 'store_staff', name: 'Store Staff', category: 'operational' },
  { systemKey: 'customer', name: 'Customer', category: 'combined' },
];

const CATEGORY_LABELS: Record<string, string> = {
  administrative: 'Administrative Roles',
  operational: 'Operational Roles',
  combined: 'Other Roles',
};

export const UserCreate = () => {
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = useForm<UserFormData>();

  const selectedRoleSystemKey = watch('roleSystemKey');

  // Fetch organizations for dropdown
  const { data: organizations } = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const response = await axiosInstance.get('/organizations');
      return Array.isArray(response.data) ? response.data : response.data.data || [];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: UserFormData) => {
      // Create user with basic info
      const createData: any = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        password: data.password,
        organizationId: data.organizationId || undefined,
      };

      // For super_admin, set the isSuperAdmin flag
      if (data.roleSystemKey === 'super_admin') {
        createData.isSuperAdmin = true;
      }

      const response = await axiosInstance.post('/users', createData);
      const userId = response.data.id;

      // If an organization is selected and it's not super_admin, assign roles via the permissions API
      if (data.organizationId && data.roleSystemKey !== 'super_admin') {
        // Find the role in the organization by system key
        try {
          const rolesResponse = await axiosInstance.get(`/permissions/roles?organizationId=${data.organizationId}`);
          const roles = rolesResponse.data;
          const matchingRole = roles.find((r: any) => r.systemKey === data.roleSystemKey);

          if (matchingRole) {
            await axiosInstance.post(`/permissions/users/${userId}/roles`, {
              roleIds: [matchingRole.id],
            });
          }
        } catch (roleError) {
          console.warn('Could not assign role:', roleError);
        }
      }

      return response.data;
    },
    onSuccess: () => {
      showMessage('User created successfully', 'success');
      navigate('/users');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to create user', 'error');
    },
  });

  const onSubmit = (data: UserFormData) => {
    createMutation.mutate(data);
  };

  // Group roles by category
  const rolesByCategory = SYSTEM_ROLES.reduce((acc, role) => {
    const category = role.category || 'combined';
    if (!acc[category]) acc[category] = [];
    acc[category].push(role);
    return acc;
  }, {} as Record<string, typeof SYSTEM_ROLES>);

  return (
    <Box>
      <Typography variant="h4" mb={3}>
        Create User
      </Typography>

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <TextField
              {...register('firstName', { required: 'First name is required' })}
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
              margin="normal"
              fullWidth
              label="First Name"
              autoFocus
            />

            <TextField
              {...register('lastName', { required: 'Last name is required' })}
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
              margin="normal"
              fullWidth
              label="Last Name"
            />

            <TextField
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              error={!!errors.email}
              helperText={errors.email?.message}
              margin="normal"
              fullWidth
              label="Email"
              type="email"
            />

            <TextField
              {...register('phone', { required: 'Phone is required' })}
              error={!!errors.phone}
              helperText={errors.phone?.message}
              margin="normal"
              fullWidth
              label="Phone"
            />

            <TextField
              {...register('password', {
                required: 'Password is required',
                minLength: {
                  value: 6,
                  message: 'Password must be at least 6 characters',
                },
              })}
              error={!!errors.password}
              helperText={errors.password?.message}
              margin="normal"
              fullWidth
              label="Password"
              type="password"
            />

            <Controller
              name="roleSystemKey"
              control={control}
              rules={{ required: 'Role is required' }}
              render={({ field, fieldState }) => (
                <FormControl fullWidth margin="normal" error={!!fieldState.error}>
                  <InputLabel>Role</InputLabel>
                  <Select
                    {...field}
                    label="Role"
                    input={<OutlinedInput label="Role" />}
                  >
                    {(['administrative', 'operational', 'combined'] as const).flatMap((category) => {
                      const categoryRoles = rolesByCategory[category];
                      if (!categoryRoles?.length) return [];
                      return [
                        <ListSubheader key={`header-${category}`} sx={{ bgcolor: 'background.paper' }}>
                          {CATEGORY_LABELS[category]}
                        </ListSubheader>,
                        ...categoryRoles.map((role) => (
                          <MenuItem key={role.systemKey} value={role.systemKey}>
                            {role.name}
                          </MenuItem>
                        )),
                      ];
                    })}
                  </Select>
                  {fieldState.error && (
                    <FormHelperText>{fieldState.error.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />

            {selectedRoleSystemKey && selectedRoleSystemKey !== 'super_admin' && selectedRoleSystemKey !== 'customer' && (
              <TextField
                {...register('organizationId')}
                margin="normal"
                fullWidth
                select
                label="Organization (Optional)"
                defaultValue=""
              >
                <MenuItem value="">None</MenuItem>
                {organizations?.map((org: any) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <Box display="flex" gap={2} mt={3}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/users')}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
